import {unixTimestampMSFromUTCString} from '@wandb/weave/common/util/time';
import {createAction} from 'typesafe-actions';

import {type RootState, type ThunkResult} from '../../types/redux';
import {type Viewer} from '../viewer/types';
import * as ViewActions from '../views/actions';
import * as ViewActionsInternal from '../views/actionsInternal';
import * as ViewApi from '../views/api';
import * as ViewThunks from '../views/thunks';
import type * as ViewTypes from '../views/types';
import type * as Types from './types';

export function deinit(workspaceID: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const {viewListID: metadataListID, viewRef} = state.workspaces[workspaceID];
    dispatch(unloadWorkspace(workspaceID));
    if (viewRef != null) {
      dispatch(ViewThunks.unloadView(viewRef.id));
    }
    dispatch(ViewThunks.unloadMetadataList(metadataListID));
    dispatch(ViewActionsInternal.deleteUndoRedoHistory());
  };
}

export function load(
  workspaceID: string,
  cid: string,
  nwId: Types.NamedWorkspaceId | 'legacy'
): ThunkResult<Promise<void>> {
  return (dispatch, getState) => {
    const state = getState();
    const workspace = state.workspaces[workspaceID];
    dispatch(viewLoadStart(workspaceID));
    return dispatch(ViewThunks.load(cid)).then(view => {
      if (view.type !== workspace.viewType) {
        throw new Error(
          `Loaded view's type (${view.type}) didn't match workspace type (${workspace.viewType})`
        );
      }

      // For personal workspaces, `nwId.username` is initially parsed from the `nw` url query param.
      // This value does include username, but with dashes stripped out. This means `nwId.username`
      // will be incorrect if the user's username includes dashes. This affects some UI elements
      // related to permissions/ownership. We must correct this by grabbing the correct username
      // from the view spec itself once it's loaded.
      const isPersonalWorkspace =
        nwId !== 'legacy' && nwId.nwType === 'personal workspace';
      const nwIdWithCorrectUsername = isPersonalWorkspace
        ? {...nwId, username: view.username}
        : nwId;

      dispatch(
        showNewView(
          workspaceID,
          {type: workspace.viewType, id: cid},
          nwIdWithCorrectUsername
        )
      );

      return Promise.resolve();
    });
  };
}

export function getProjectReadonlyState(
  viewer: Viewer | undefined,
  workspace: Types.ReduxWorkspaceState,
  state: RootState
) {
  const {viewListID, viewType} = workspace;
  if (viewListID == null || viewType == null) {
    throw new Error('Invalid state');
  }
  const viewList = state.views.lists[viewListID];
  let readOnly = false;
  if (viewer == null) {
    readOnly = true;
  } else if (viewList.viewIds.length > 0) {
    // This logic isn't quite right, we only know if the project
    // is readonly if there is at least one workspace saved.
    // TODO: Handle the zero workspace case
    const view0 = state.views.views[viewList.viewIds[0]];
    readOnly = view0.project == null || view0.project.readOnly;
  }
  return readOnly;
}

export function fixView(workspaceID: string): ThunkResult<Promise<void>> {
  return (dispatch, getState) => {
    const workspace = getState().workspaces[workspaceID];
    if (workspace.defaultSpec == null) {
      throw new Error('Invalid state');
    }
    dispatch(update(workspaceID, workspace.defaultSpec));
    return Promise.resolve();
  };
}

export function update(
  workspaceID: string,
  spec: Types.WorkspaceMaterialized
): ThunkResult<void> {
  return (dispatch, getState) => {
    const workspace = getState().workspaces[workspaceID];
    if (workspace.viewRef == null) {
      throw new Error('Unexpected state');
    }
    dispatch(ViewThunks.updateViewSpec(workspace.viewRef.id, spec));
  };
}

// A create save that conflicts means a view with this name already exists
// on the server. Adopt its id so the conflict resolves against it.
async function resolveServerID(
  dispatch: Parameters<ThunkResult<void>>[0],
  getState: Parameters<ThunkResult<void>>[1],
  client: Parameters<ThunkResult<void>>[2],
  cid: string
): Promise<string | undefined> {
  const view = getState().views.views[cid];
  if (view?.id != null) {
    return view.id;
  }
  if (view == null) {
    return undefined;
  }
  const projectName = view.project?.name;
  if (projectName == null) {
    return undefined;
  }

  const views = await ViewApi.loadMetadataList(client, {
    entityName: view.project?.entityName ?? view.entityName,
    projectName,
    viewType: view.type,
    viewName: view.name,
  });
  const matchingView = views.find(({name}) => name === view.name);
  if (matchingView?.id == null) {
    return undefined;
  }

  dispatch(ViewActionsInternal.setServerID(cid, matchingView.id));
  return matchingView.id;
}

// Internal actions

export function reloadWorkspaceViewFromServer(
  workspaceID: string
): ThunkResult<Promise<void>> {
  return async (dispatch, getState, client) => {
    const initialState = getState();
    const cid = initialState.workspaces[workspaceID]?.viewRef?.id;
    if (cid == null || !canSafelyReplaceViewFromServer(initialState, cid)) {
      return;
    }

    const view = initialState.views.views[cid];
    const serverID = view.id;
    const oldPartRef = view.partRef;
    if (serverID == null || oldPartRef == null) {
      return;
    }

    const serverMetadata = await ViewApi.fetchViewSyncMetadata(
      client,
      serverID
    );
    if (!isServerViewSpecNewer(serverMetadata, view)) {
      return;
    }

    const latestState = getState();
    if (
      latestState.workspaces[workspaceID]?.viewRef?.id !== cid ||
      !canSafelyReplaceViewFromServer(latestState, cid)
    ) {
      return;
    }
    await dispatch(ViewThunks.load(cid));
    if (getState().views.views[cid]?.partRef?.id !== oldPartRef.id) {
      dispatch(ViewThunks.deleteObject(oldPartRef));
    }
  };
}

// Save conflict resolution: drop this tab's unsaved edits and load the newer
// spec from the server. Reloading also clears `saveConflict`.
export function discardWorkspaceSaveConflict(
  workspaceID: string
): ThunkResult<Promise<void>> {
  return async (dispatch, getState, client) => {
    const cid = getState().workspaces[workspaceID]?.viewRef?.id;
    const oldPartRef =
      cid == null ? undefined : getState().views.views[cid]?.partRef;
    if (cid == null || oldPartRef == null) {
      return;
    }
    await resolveServerID(dispatch, getState, client, cid);
    await dispatch(ViewThunks.load(cid));
    if (getState().views.views[cid]?.partRef?.id !== oldPartRef.id) {
      dispatch(ViewThunks.deleteObject(oldPartRef));
    }
  };
}

// Save conflict resolution: keep this tab's spec. The save is still guarded,
// against the server's current updatedAt, so a write that lands after this
// check raises a new conflict instead of being overwritten.
export function overwriteWorkspaceSaveConflict(
  workspaceID: string
): ThunkResult<Promise<void>> {
  return async (dispatch, getState, client) => {
    const viewRef = getState().workspaces[workspaceID]?.viewRef;
    if (viewRef == null) {
      return;
    }
    const serverID = await resolveServerID(
      dispatch,
      getState,
      client,
      viewRef.id
    );
    if (serverID == null) {
      return;
    }
    const serverUpdatedAt = await ViewApi.fetchViewUpdatedAt(client, serverID);
    dispatch(
      ViewActionsInternal.overwriteSaveConflict(viewRef.id, serverUpdatedAt)
    );
    dispatch(ViewActions.save(viewRef));
  };
}

// Note: View table `updatedAt` only has second-level precision.
// We are aware that writes within the same second will compare as unchanged,
// and decided this was an acceptable tradeoff to migrating the db.
export function isServerViewSpecNewer(
  server: ViewApi.ViewSyncMetadata,
  local: Pick<ViewTypes.View, 'updatedAt'>
): boolean {
  return server.updatedAtMS > unixTimestampMSFromUTCString(local.updatedAt);
}

export function canSafelyReplaceViewFromServer(
  state: Pick<RootState, 'views'>,
  cid: string
): boolean {
  const view = state.views.views[cid];
  if (
    state.views.loading ||
    view == null ||
    view.loading ||
    view.partRef == null ||
    view.saving ||
    view.modified
  ) {
    return false;
  }
  const currentUndoId =
    state.views.undoActions[state.views.undoActions.length - 1]?.id;
  return view.undoIdAtLastSave === currentUndoId;
}

export const initStart = createAction(
  '@workspace/initStart',
  action =>
    (
      id: string,
      viewType: Types.ViewTypes,
      viewListID: string,
      defaultSpec: Types.WorkspaceMaterialized,
      relatedId: Types.WsRelatedId,
      entityName: string,
      projectName: string
    ) =>
      action({
        id,
        viewListID,
        viewType,
        defaultSpec,
        relatedId,
        entityName,
        projectName,
      })
);

export const updateDefaultSpec = createAction(
  '@workspace/updateDefaultSpec',
  action => (id: string, defaultSpec: Types.WorkspaceMaterialized) =>
    action({id, defaultSpec})
);

export const unloadWorkspace = createAction(
  '@workspace/deinitStart',
  action => (id: string) => action({id})
);

export const viewLoadStart = createAction(
  '@workspace/viewLoadStart',
  action => (id: string) => action({id})
);

export const showNewView = createAction(
  '@workspace/showNewView',
  action =>
    (
      workspaceId: string,
      viewRef: Types.ViewID,
      nwId: Types.NamedWorkspaceId | 'legacy'
    ) =>
      action({workspaceId, nwId, viewRef})
);

export const newWorkspaceViews = createAction(
  '@workspace/newWorkspaceViews',
  action => (workspaceId: string, viewCids: string[]) =>
    action({workspaceId, viewCids})
);

export const removeWorkspaceView = createAction(
  '@workspace/removeWorkspaceView',
  action => (workspaceId: string, cidToRemove: string) =>
    action({workspaceId, cidToRemove})
);

export const displayWorkspaceError = createAction(
  '@workspace/showError',
  action => (workspaceId: string, error: Types.WorkspaceError) =>
    action({workspaceId, error})
);

export type ActionType = ReturnType<
  | typeof initStart
  | typeof updateDefaultSpec
  | typeof unloadWorkspace
  | typeof viewLoadStart
  | typeof showNewView
  | typeof newWorkspaceViews
  | typeof removeWorkspaceView
  | typeof displayWorkspaceError
>;
